import { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, TextField, Button,
  useTheme, alpha, Divider, IconButton, FormControlLabel,
  Checkbox, InputAdornment, Card, CardContent, Grid,
  Alert, Snackbar
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  LockOutlined as LockOutlinedIcon,
  Email as EmailIcon,
  Login as LoginIcon,
  ArrowForward as ArrowForwardIcon,
  Science as ScienceIcon,
  Business as BusinessIcon,
  Biotech as BiotechIcon,
  LocalHospital as LocalHospitalIcon
} from '@mui/icons-material';
import { ChangeEvent, FormEvent } from 'react';

interface LoginPageProps {
  onLogin: (email: string, password: string, rememberMe: boolean) => Promise<{ success: boolean; message?: string }>;
  isAuthenticated: boolean;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, isAuthenticated }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'info' | 'success' | 'warning'>('success');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }

    // Check if coming from registration or password reset
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('registered') === 'true') {
      setSnackbarMessage('Registration successful! Your account is pending approval. You will be notified when you can log in.');
      setSnackbarSeverity('info');
      setOpenSnackbar(true);
    } else if (searchParams.get('accountApproved') === 'true') {
      setSnackbarMessage('Your account has been approved! You can now log in.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } else if (searchParams.get('passwordReset') === 'true') {
      setSnackbarMessage('Password reset successful! You can now log in with your new password.');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = event.target as HTMLInputElement;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  // Fixed handleSubmit function
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    console.log('Form submit triggered!'); // Debug log
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login with:', { 
        email: formData.email, 
        password: '***', 
        rememberMe: formData.rememberMe 
      }); // Debug log

      const loginResult = await onLogin(formData.email, formData.password, formData.rememberMe);
      
      console.log('Login result:', loginResult); // Debug log

      if (loginResult.success) {
        navigate('/');
      } else {
        setError(loginResult.message || 'Invalid email or password. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err); // Debug log
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Grid container spacing={4}>
        {/* Left side - Login Form */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={3}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Form header */}
            <Box
              sx={{
                p: 3,
                background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                color: 'white'
              }}
            >
              <Typography variant="h4" fontWeight={600}>
                Login
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                Access your VCG portal account
              </Typography>
            </Box>

            {/* Form content */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1
              }}
            >
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              <TextField
                required
                fullWidth
                margin="normal"
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <TextField
                required
                fullWidth
                margin="normal"
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      name="rememberMe"
                      color="secondary"
                      disabled={loading}
                    />
                  }
                  label="Remember me"
                />
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  onClick={() => navigate('/forgot-password')}
                  sx={{ textTransform: 'none' }}
                  disabled={loading}
                >
                  Forgot password?
                </Button>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="secondary"
                size="large"
                disabled={loading}
                startIcon={<LoginIcon />}
                sx={{
                  mt: 2,
                  mb: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Don't have an account yet?
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/register')}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Create your VCG portal account
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Right side - Benefits and information */}
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Welcome message with gradient */}
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '40%',
                  height: '100%',
                  opacity: 0.1,
                  background: `radial-gradient(circle, ${theme.palette.secondary.main} 0%, transparent 70%)`,
                  transform: 'translateX(30%)'
                }}
              />

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 2
                  }}
                >
                  Welcome Back:
                </Typography>

                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.7,
                    fontSize: '1rem'
                  }}
                >
                  Access specialized value chains, investment intelligence, and live opportunities across 35+ Saudi industries.
                  Discover where innovation is needed, explore active projects, publish your investment offerings, connect with partners, and act through
                  our smart, data-driven decision-support platform.
                </Typography>
              </Box>
            </Paper>

            {/* Industry features cards */}
            <Grid container spacing={2}>
              {[
                {
                  icon: <BusinessIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />,
                  title: "Saudi Value Chains",
                  description: "Explore structured views of 35+ Saudi sectors and their value chains – revealing where innovation fits, localization paths, growth areas, support infrastructure, and top investment entry points."
                },
                {
                  icon: <ScienceIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />,
                  title: "Investment Intelligence",
                  description: "Track real-time data, trends, and performance across Saudi industries. Identify where innovation and high-growth sectors are emerging, and make better investment decisions based on actionable insight."
                },
                {
                  icon: <LocalHospitalIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />,
                  title: "Opportunity Marketplace",
                  description: "Discover active projects and innovation opportunities, and showcase investment offers. Engage directly with investors, innovators, and project owners, participate in deals, and form partnerships."
                },
                {
                  icon: <BiotechIcon fontSize="large" sx={{ color: theme.palette.secondary.main }} />,
                  title: "Industry Network",
                  description: "Engage with verified organizations, experts, and institutions powering Saudi Arabia's innovation and economic growth. Build partnerships that turn opportunities into ventures."
                }
              ].map((feature, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    elevation={1}
                    sx={{
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                      },
                      height: '100%'
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ mb: 2 }}>
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        }}
                      >
                        {feature.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Help information */}
            <Paper
              variant="outlined"
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.info.main, 0.05),
                borderColor: alpha(theme.palette.info.main, 0.2)
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} color="info.dark" gutterBottom>
                Need assistance with your account?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contact our support team at <Box component="span" sx={{ fontWeight: 500 }}>support@value-chain.global</Box>
              </Typography>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default LoginPage;